import React, { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { buildAdminEvents } from '~/app/pages/Transfers/_utils/admin';
import { ROUTE } from '~/app/routes';
import { CopyInput, Details, Loading, RenderFields } from '~/components';
import { useAccountNumber, useBankAccount, useEntity } from '~/hooks';
import { useTransfer } from '~/hooks/useTransfers';
import { useNavigate } from '~/lib/navigation';
import { TransferHeader } from '~/lib/transfers';
import { AdminTransfer } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

import { InfoForm } from './_components/Layout';
import { Timeline } from './_components/Timeline';

interface Params {
  id: string;
}

const formatData = (transfer: AdminTransfer): Record<string, unknown> => {
  return { ...transfer };
};

export const PageTransfersAdmin: FC = () => {
  const { isAuthorized } = useSessionStore();
  const { id } = useParams<keyof Params>() as Params;
  const navigate = useNavigate();
  const { addDangerNotification } = useNotificationStore();

  const {
    response: bankAccount,
    isLoading: isBankAccountLoading,
    createRequest: fetchBankAccount,
  } = useBankAccount({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: accountNumber,
    isLoading: isAccountNumberLoading,
    createRequest: fetchAccountNumber,
  } = useAccountNumber({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const { response: entity, createRequest: fetchEntity } = useEntity({
    onSuccess: () => setIsLoadingEntity(false),
    onError: (error) => {
      setIsLoadingEntity(false);
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const [isLoadingEntity, setIsLoadingEntity] = useState(false);

  const { response: transfer, isLoading: isTransferLoading } = useTransfer<AdminTransfer>('admin')({
    initialParams: { id },
    onSuccess: async (response) => {
      if (response.bankAccountId) {
        try {
          const currentBankAccount = await fetchBankAccount({ id: response.bankAccountId });

          if (!currentBankAccount) {
            return;
          }

          if (currentBankAccount.defaultAccountNumberId) {
            fetchAccountNumber({ id: currentBankAccount.defaultAccountNumberId });
          }

          if (isAuthorized({ permission: 'entities', permissionLevel: 'read' })) {
            fetchEntity({ id: currentBankAccount.owners?.[0] });
            setIsLoadingEntity(true);
          }
        } catch (error) {
          addDangerNotification({ content: (error as Error).message });
        }
      }
    },
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const formattedTransfer = useMemo(() => (transfer ? formatData(transfer) : undefined), [transfer]);

  const rawTransferData = useMemo(() => {
    if (!transfer) return undefined;
    const convertToSerializable = (obj: any): any => {
      if (obj instanceof Date) {
        return obj.toISOString();
      }
      if (Array.isArray(obj)) {
        return obj.map(convertToSerializable);
      }
      if (obj && typeof obj === 'object') {
        const converted: Record<string, unknown> = {};
        Object.entries(obj).forEach(([key, value]) => {
          converted[key] = convertToSerializable(value);
        });
        return converted;
      }
      return obj;
    };
    return convertToSerializable(transfer) as Record<string, unknown>;
  }, [transfer]);

  const formMethods = useForm<AdminTransfer>();

  useEffect(() => {
    if (transfer) {
      formMethods.setValue('description', transfer.description || '-');
      formMethods.setValue('detailsType', transfer.detailsType || null);
      formMethods.setValue('type', transfer.type);
    }
  }, [transfer, formMethods]);

  const isDataLoading = useMemo(
    () => isAccountNumberLoading || isBankAccountLoading || isTransferLoading || isLoadingEntity,
    [isAccountNumberLoading, isBankAccountLoading, isTransferLoading, isLoadingEntity]
  );

  const isCredit = transfer?.type === 'credit';
  const status = 'completed';

  return (
    <>
      <Loading isLoading={isDataLoading}>
        {transfer && (
          <TransferHeader
            amount={transfer.amount}
            createdAt={transfer.createdAt}
            isAdding={isCredit}
            direction={{ type: isCredit ? 'incoming' : 'outgoing' }}
            status={status}
            transferId={transfer.adminTransferId}
            transferType="admin"
          />
        )}

        {accountNumber && bankAccount && transfer && !isLoadingEntity && (
          <Timeline
            entries={buildAdminEvents({
              transfer: { ...transfer },
              bankAccount: bankAccount,
              accountNumber: accountNumber,
              entityType: entity ? (entity.type.toLowerCase() as 'person' | 'business') : undefined,
            })}
          />
        )}

        <FormProvider {...formMethods}>
          <InfoForm autoComplete="off">
            {RenderFields<AdminTransfer>({
              sections: [
                {
                  headline: 'Information',
                  fields: [
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value }) => {
                        return <CopyInput value={String(value)} />;
                      },
                    },

                    {
                      id: 'detailsType',
                      label: 'Details Type',
                      children: ({ value }) => {
                        return <CopyInput value={String(value || '-')} />;
                      },
                    },
                    {
                      id: 'type',
                      label: 'Type',
                      children: ({ value }) => {
                        return <CopyInput value={String(value)} />;
                      },
                    },
                  ],
                },
              ],
            })}
            <Details formattedData={formattedTransfer} rawData={rawTransferData} />
          </InfoForm>
        </FormProvider>
      </Loading>
    </>
  );
};
